import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FilterHeader = ({ onFiltersChange, onComparisonToggle, comparisonMode }) => {
  const [activeFilters, setActiveFilters] = useState({
    subscriptionPlans: [],
    tenantStatus: [],
    regions: []
  });

  const [dropdownOpen, setDropdownOpen] = useState({
    plans: false,
    status: false,
    regions: false
  });

  const filterOptions = {
    subscriptionPlans: [
      { value: 'starter', label: 'Starter Plan', count: 1247 },
      { value: 'professional', label: 'Professional Plan', count: 892 },
      { value: 'enterprise', label: 'Enterprise Plan', count: 456 },
      { value: 'custom', label: 'Custom Plan', count: 252 }
    ],
    tenantStatus: [
      { value: 'active', label: 'Active', count: 2156 },
      { value: 'trial', label: 'Trial', count: 423 },
      { value: 'suspended', label: 'Suspended', count: 89 },
      { value: 'churned', label: 'Churned', count: 179 }
    ],
    regions: [
      { value: 'north-america', label: 'North America', count: 1456 },
      { value: 'europe', label: 'Europe', count: 892 },
      { value: 'asia-pacific', label: 'Asia Pacific', count: 345 },
      { value: 'latin-america', label: 'Latin America', count: 154 }
    ]
  };

  const toggleFilter = (category, value) => {
    const newFilters = { ...activeFilters };
    const currentFilters = newFilters[category];
    
    if (currentFilters.includes(value)) {
      newFilters[category] = currentFilters.filter(f => f !== value);
    } else {
      newFilters[category] = [...currentFilters, value];
    }
    
    setActiveFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const clearAllFilters = () => {
    const clearedFilters = {
      subscriptionPlans: [],
      tenantStatus: [],
      regions: []
    };
    setActiveFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const getActiveFilterCount = () => {
    return Object.values(activeFilters).flat().length;
  };

  const FilterDropdown = ({ category, title, options }) => (
    <div className="relative">
      <Button
        variant="outline"
        onClick={() => setDropdownOpen(prev => ({ 
          ...Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: false }), {}),
          [category]: !prev[category] 
        }))}
        className="flex items-center space-x-2"
      >
        <span>{title}</span>
        {activeFilters[category].length > 0 && (
          <span className="bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full">
            {activeFilters[category].length}
          </span>
        )}
        <Icon name="ChevronDown" size={16} />
      </Button>

      {dropdownOpen[category] && (
        <div className="absolute top-full left-0 mt-2 w-64 bg-popover border border-border rounded-lg shadow-elevation-3 z-50">
          <div className="p-4 space-y-2">
            {options.map((option) => (
              <label
                key={option.value}
                className="flex items-center justify-between p-2 rounded-md hover:bg-muted/50 cursor-pointer"
              >
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={activeFilters[category].includes(option.value)}
                    onChange={() => toggleFilter(category, option.value)}
                    className="w-4 h-4 text-primary bg-input border-border rounded focus:ring-primary focus:ring-2"
                  />
                  <span className="text-sm text-text-primary">{option.label}</span>
                </div>
                <span className="text-xs text-text-secondary bg-muted px-2 py-1 rounded">
                  {option.count}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        {/* Filter Controls */}
        <div className="flex flex-wrap items-center gap-4">
          <FilterDropdown
            category="subscriptionPlans"
            title="Subscription Plans"
            options={filterOptions.subscriptionPlans}
          />
          
          <FilterDropdown
            category="tenantStatus"
            title="Tenant Status"
            options={filterOptions.tenantStatus}
          />
          
          <FilterDropdown
            category="regions"
            title="Regions"
            options={filterOptions.regions}
          />

          {getActiveFilterCount() > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              className="text-text-secondary hover:text-text-primary"
            >
              <Icon name="X" size={16} />
              Clear All ({getActiveFilterCount()})
            </Button>
          )}
        </div>

        {/* Action Controls */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-text-secondary">Comparison Mode</span>
            <button
              onClick={onComparisonToggle}
              className={`
                relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200
                ${comparisonMode ? 'bg-primary' : 'bg-muted'}
              `}
            >
              <span
                className={`
                  inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200
                  ${comparisonMode ? 'translate-x-6' : 'translate-x-1'}
                `}
              />
            </button>
          </div>

          <Button variant="outline" size="sm">
            <Icon name="Download" size={16} />
            Export
          </Button>

          <Button variant="outline" size="sm">
            <Icon name="RefreshCw" size={16} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Active Filters Display */}
      {getActiveFilterCount() > 0 && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-text-secondary">Active filters:</span>
            {Object.entries(activeFilters).map(([category, filters]) =>
              filters.map(filter => {
                const option = filterOptions[category].find(opt => opt.value === filter);
                return (
                  <span
                    key={`${category}-${filter}`}
                    className="inline-flex items-center space-x-1 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm"
                  >
                    <span>{option?.label}</span>
                    <button
                      onClick={() => toggleFilter(category, filter)}
                      className="hover:bg-primary/20 rounded-full p-0.5"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </span>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterHeader;